import * as Styles from './styles';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../third-party-integration/amplitude';
import { AMPLITUDE_EVENTS } from '../../constants';
import {
  BUTTON_VARIANTS,
  Divider,
  IconClose,
} from 'cdk-radial';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAdoptEaseStore } from './useAdoptEaseStore';
import { useWebViewStore } from '../webview/useWebViewStore';
import launchExternal from '../../util/launchExternal';

import { Button } from './../../components/Buttons/Button';

interface AdoptEaseApplicationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  iconColor: string;
  iconText: string;
  name: string;
  description: string;
  cdkUUrl: string;
  id: string;
  onOpenClick: Function;
}

export default function AdoptEaseApplicationDialog({
  isOpen,
  onClose,
  iconColor,
  iconText,
  name,
  cdkUUrl,
  id,
  onOpenClick,
}: AdoptEaseApplicationDialogProps) {

  const { trackEvent } = useAnalytics();
  const { t } = useTranslation();
  const history = useHistory();
  const { setCustomWebView } = useWebViewStore();
  const [hasTraining, setHasTraining] = useState(false);
  const [message, setMessage] = useState('');
  const [trainingMessage, setTrainingMessage] = useState('');

  const neverUsedApps = useAdoptEaseStore((state: any) => state.neverUsedApps);
  const adoptEaseDefaultConfigData = useAdoptEaseStore((state: any) => state.adoptEaseDefaultConfigData);
  const userLocale = localStorage.getItem('userLocale');

  useEffect(() => {
    if(adoptEaseDefaultConfigData){
      const locale = userLocale?.replace('_', '') || 'enUS';
      const appEntry = neverUsedApps.find((app: any) => app.appId === id);
      const config = adoptEaseDefaultConfigData.find((cfg: any) => cfg.appId === id);
      setHasTraining(appEntry?.courseTaken ?? false);
      setMessage(config?.message?.[locale] || '');
      setTrainingMessage(config?.messageTraining?.[locale] || '');
    }
  }, [adoptEaseDefaultConfigData, neverUsedApps, id, userLocale]);

  const hasCdkUTraining = !hasTraining && !!cdkUUrl;

  const handleCancel = () => {
    trackEvent(AMPLITUDE_EVENTS.ADOPTEASE_APPLICATION_MODAL_CANCEL, { Application: name });
    onClose();
  };

  const handleDismiss = () => {
    trackEvent(AMPLITUDE_EVENTS.ADOPTEASE_APPLICATION_MODAL_DISMISS, { Application: name });
    onClose();
  };

  const handleOpen = () => {
    onClose();
    trackEvent(AMPLITUDE_EVENTS.OPEN_APPLICATION_FROM_ADOPTEASE, { Application: name });
    trackEvent(AMPLITUDE_EVENTS.OPEN_APPLICATION, { Application: name });
    onOpenClick();
    const openedApps: string[] = JSON.parse(localStorage.getItem('opened-apps') || '[]');
    if (!openedApps.includes(id)) {
      openedApps.push(id);
      localStorage.setItem('opened-apps', JSON.stringify(openedApps));
    }
  };

  const handleTraining = () => {
    trackEvent(AMPLITUDE_EVENTS.OPEN_APPLICATION_TRAINING_FROM_ADOPTEASE, { Application: name });
    if (!launchExternal(cdkUUrl)) {
      history.push(`/unify/view/${id}/cta`);
      setCustomWebView({ id, url: cdkUUrl });
    }
    onClose();
  };

  return (
    <Styles.StyledApplicationDialog
      isOpen={isOpen}
      id="recomm-dialog"
      data-testid="adoptease-recomm-dialog-is"
      onClose={handleDismiss}
      hideHeader
      hideCloseButton={true}
      disableOverlayClicked={true}
      title=''
    >
      <Styles.StyledDiv style={{ padding: '10px 16px 16px 5px', justifyContent: 'space-between', height: '70px' }}>
        <Styles.CardHeader>
          <Styles.ProductIcon iconColor={iconColor} data-testid="app-icon">
            {iconText || ''}
          </Styles.ProductIcon>
          <div style={{ paddingLeft: '10px' }}>
            <Styles.CardTitle>{name}</Styles.CardTitle>
            <div style={{ fontWeight: '400' }}>CDK Global</div>
          </div>
        </Styles.CardHeader>
        <Styles.RoundedIconButton
          data-testid="on-close-icon"
          size="small"
          icon={<IconClose />}
          onClick={handleDismiss}
          text="BannerClose"
        />
      </Styles.StyledDiv>

      <Divider data-testid="header-divider" />

      <div style={{ paddingTop: '15px', fontSize: '20px', fontWeight: '500' }}>
        {`${name}  ${t('adoptease.appAvailable')}`}
      </div>

      {!hasTraining && cdkUUrl && cdkUUrl !== "" ? (
        <div style={{ paddingTop: '5px', fontSize: '16px', fontWeight: '350' }} data-testid="no-usage-no-training">
          {message}
        </div>
      ) : (
        <div style={{ paddingTop: '5px', fontSize: '16px', fontWeight: '350' }} data-testid="no-usage">
          {trainingMessage}
        </div>
      )}

      <Styles.StyledAllButtonDiv>
        <Styles.StyledButtonDiv>
          <Styles.StyledCancelButton  style={{color: '#155EBF'}}
            text={t('adoptease.cancel')}
            data-testid="cancel-button"
            onClick={handleCancel}
          />
          <Button
            style={{ backgroundColor: '#E8F1FC', color: '#155EBF', fontSize: '14px', fontWeight: '500', marginBottom: '10px', marginRight: '10px', display: 'grid' }}
            variant={BUTTON_VARIANTS.PRIMARY}
            text={`${t('adoptease.open')}  ${name}`}
            onClick={handleOpen}
          />
          {hasCdkUTraining && (
            <Button
              style={{ backgroundColor: '#196FE1', fontSize: '14px', fontWeight: '500' }}
              variant={BUTTON_VARIANTS.PRIMARY}
              text={t('adoptease.take')}
              onClick={handleTraining}
            />
          )}
        </Styles.StyledButtonDiv>
      </Styles.StyledAllButtonDiv>
    </Styles.StyledApplicationDialog>
  );
}
